import React from 'react';
import {
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    HStack,
    VStack,
    Box,
    Text,
    Icon,
    Spinner,
    Progress,
    List,
    ListItem,
    Divider,
} from '@chakra-ui/react';
import { AiOutlineFileExcel, AiOutlineUpload } from 'react-icons/ai';
import { useModal } from '~/hooks/useModal';
import { useAgreementCsvImport } from '~/pages/master-data/ui/GeneralAgreements/hooks/useAgreementCsvImport';
import {
    ISSUE_LABELS,
    ISSUE_SEVERITY,
    IssueSeverity,
    RowIssue,
} from '~/pages/master-data/ui/GeneralAgreements/lib/csvImport';
import { Rahmenvertrag } from '~/gql/ucpw/graphql';

const HINT_TEXT =
    'Bitte prüfen Sie die importierten RV-Positionen und löschen Sie ggf. die Rahmenvereinbarung, falls etwas nicht stimmt.';

export const GeneralAgreementsCsvImportModal = (props: {
    id?: string;
    setActiveGeneralAgreement?: (rv: Rahmenvertrag) => void;
}) => {
    const { dispatch, onClose } = useModal();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [file, setFile] = React.useState<File | null>(null);

    const { phase, errorMessage, progress, report, preview, analyzing, analyze, confirmImport, backToSelect } =
        useAgreementCsvImport({
            onImported: props.setActiveGeneralAgreement,
        });

    const close = () => {
        dispatch?.({ type: 'resetState' });
        onClose?.();
    };

    const onBack = () => {
        setFile(null);
        backToSelect();
    };

    const acceptFile = (selected: File | null) => {
        // Only accept .csv; ignore anything else (e.g. a wrong file dropped).
        if (selected && !isCsvFile(selected)) return;
        setFile(selected);
    };

    return (
        <ModalContent maxWidth="container.md" rounded="none">
            <ModalHeader
                borderBottomWidth={1}
                borderColor="gray.200"
                p={5}
            >
                Rahmenvereinbarung aus CSV importieren
            </ModalHeader>

            <ModalBody p={5}>
                {phase === 'select' && (
                    <SelectPhase
                        file={file}
                        fileInputRef={fileInputRef}
                        onFile={acceptFile}
                    />
                )}

                {phase === 'preview' && preview && <PreviewPhase preview={preview} />}

                {phase === 'processing' && <ProcessingPhase progress={progress} />}

                {phase === 'error' && (
                    <Box>
                        <Text fontWeight="semibold" color="red.500" mb={2}>
                            Import nicht möglich
                        </Text>
                        <Text color="gray.700">{errorMessage}</Text>
                        <Text color="gray.500" mt={3} fontSize="sm">
                            Es wurde nichts importiert.
                        </Text>
                    </Box>
                )}

                {phase === 'report' && report && <ReportPhase report={report} />}
            </ModalBody>

            <ModalFooter borderTopWidth={1} borderColor="gray.200" p={5}>
                {phase === 'select' && (
                    <HStack>
                        <Button data-test-id="button-cancel" variant="outline" onClick={close}>
                            Abbrechen
                        </Button>
                        <Button
                            data-test-id="button-preview"
                            colorScheme="blue"
                            isDisabled={!file}
                            isLoading={analyzing}
                            loadingText="Analysiere"
                            onClick={() => file && analyze(file)}
                        >
                            Vorschau
                        </Button>
                    </HStack>
                )}

                {phase === 'preview' && (
                    <HStack>
                        <Button data-test-id="button-cancel" variant="outline" onClick={close}>
                            Abbrechen
                        </Button>
                        <Button data-test-id="button-back" variant="outline" onClick={onBack}>
                            Andere Datei
                        </Button>
                        <Button
                            data-test-id="button-import"
                            colorScheme="blue"
                            isDisabled={!preview || preview.stammdatenMissing || preview.importable === 0}
                            onClick={confirmImport}
                        >
                            Importieren
                        </Button>
                    </HStack>
                )}

                {phase === 'processing' && (
                    <Button colorScheme="blue" isLoading loadingText="Import läuft" isDisabled>
                        Import
                    </Button>
                )}

                {(phase === 'error' || phase === 'report') && (
                    <Button data-test-id="button-close" colorScheme="blue" onClick={close}>
                        Schließen
                    </Button>
                )}
            </ModalFooter>
        </ModalContent>
    );
};

// ===============================================
// PHASES
// ===============================================

function SelectPhase({
    file,
    fileInputRef,
    onFile,
}: {
    file: File | null;
    fileInputRef: React.RefObject<HTMLInputElement>;
    onFile: (file: File | null) => void;
}) {
    const [isDragging, setIsDragging] = React.useState(false);

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const onDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        onFile(e.dataTransfer.files?.[0] || null);
    };

    return (
        <VStack align="stretch" spacing={4}>
            <Text color="gray.600" fontSize="sm">
                Wählen Sie eine CSV-Datei (getrennt mit Semikolon, Spalten:
                Nummer&nbsp;;&nbsp;Beschreibung&nbsp;;&nbsp;Preis&nbsp;;&nbsp;Einheit, ohne
                Kopfzeile) oder ziehen Sie sie hierher. Der Dateiname wird zur Bezeichnung der
                Rahmenvereinbarung.
            </Text>

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={(e) => onFile(e.target.files?.[0] || null)}
                data-test-id="csv-file-input"
            />

            <Box
                borderWidth={1}
                borderStyle="dashed"
                borderColor={isDragging ? 'blue.400' : 'gray.300'}
                bg={isDragging ? 'blue.50' : undefined}
                rounded="md"
                p={8}
                textAlign="center"
                cursor="pointer"
                transition="border-color 0.15s, background-color 0.15s"
                _hover={{ borderColor: 'blue.400', bg: 'gray.50' }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={onDragOver}
                onDragEnter={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                data-test-id="csv-dropzone"
            >
                {/* pointer-events:none so drag events always target the Box, not children */}
                <Box pointerEvents="none">
                    {file ? (
                        <HStack justify="center" spacing={3}>
                            <Icon as={AiOutlineFileExcel} boxSize={6} color="green.500" />
                            <Text fontWeight="medium">{file.name}</Text>
                        </HStack>
                    ) : (
                        <VStack spacing={2} color="gray.500">
                            <Icon as={AiOutlineUpload} boxSize={8} />
                            <Text>{isDragging ? 'CSV-Datei hier ablegen' : 'CSV-Datei auswählen oder hierher ziehen'}</Text>
                        </VStack>
                    )}
                </Box>
            </Box>
        </VStack>
    );
}

function ProcessingPhase({ progress }: { progress: { done: number; total: number } }) {
    const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
        <VStack align="stretch" spacing={4} py={4}>
            <HStack spacing={3}>
                <Spinner size="sm" color="blue.500" />
                <Text>
                    {progress.total > 0
                        ? `Positionen werden importiert (${progress.done} von ${progress.total})…`
                        : 'Datei wird verarbeitet…'}
                </Text>
            </HStack>
            {progress.total > 0 && (
                <Progress value={pct} size="sm" colorScheme="blue" rounded="full" />
            )}
        </VStack>
    );
}

function ReportPhase({ report }: { report: NonNullable<ReturnType<typeof useAgreementCsvImport>['report']> }) {
    return (
        <VStack align="stretch" spacing={4}>
            <Box>
                <Text fontWeight="semibold" fontSize="lg">
                    {report.imported} von {report.totalRows} Positionen importiert
                </Text>
                <Text color="gray.600" fontSize="sm">
                    Rahmenvereinbarung: „{report.bezeichnung}“
                    {!report.agreementCreated && ' – nicht angelegt (keine importierbaren Zeilen)'}
                </Text>
            </Box>

            <IssueDetailList issues={report.issues} nameTruncated={report.nameTruncated} />

            {report.agreementCreated && (
                <Text color="gray.600" fontSize="sm">
                    {HINT_TEXT}
                </Text>
            )}
        </VStack>
    );
}

function PreviewPhase({ preview }: { preview: NonNullable<ReturnType<typeof useAgreementCsvImport>['preview']> }) {
    return (
        <VStack align="stretch" spacing={4}>
            <Box>
                <Text fontWeight="semibold" fontSize="lg">
                    {preview.importable} von {preview.totalRows} Positionen werden importiert
                </Text>
                <Text color="gray.600" fontSize="sm">
                    Rahmenvereinbarung: „{preview.bezeichnung}“ · gültig {formatDate(preview.gueltigAb)} – {formatDate(preview.gueltigBis)}
                </Text>
            </Box>

            {preview.stammdatenMissing && (
                <Box borderWidth={1} borderColor="red.200" bg="red.50" rounded="md" p={3}>
                    <Text fontWeight="semibold" color="red.600" fontSize="sm">
                        Leistungs-Stammdaten („Sonstiges“) fehlen
                    </Text>
                    <Text color="red.600" fontSize="sm">
                        Der Import ist erst möglich, wenn die Stammdaten angelegt sind.
                    </Text>
                </Box>
            )}

            {preview.importable === 0 && !preview.stammdatenMissing && (
                <Text color="gray.600" fontSize="sm">
                    Es gibt keine importierbaren Zeilen – es wird keine Rahmenvereinbarung angelegt.
                </Text>
            )}

            <IssueDetailList issues={preview.issues} nameTruncated={preview.nameTruncated} />
        </VStack>
    );
}

// ===============================================
// HELPERS
// ===============================================

const SEVERITY_DOT: Record<IssueSeverity, { color: string; title: string }> = {
    error: { color: 'red.500', title: 'Wird nicht importiert' },
    warning: { color: 'yellow.400', title: 'Wurde angepasst – bitte prüfen' },
};

/** Shared scrollable issue list, used by both the preview and the report. */
function IssueDetailList({ issues, nameTruncated = false }: { issues: RowIssue[]; nameTruncated?: boolean }) {
    const total = issues.length + (nameTruncated ? 1 : 0);
    if (total === 0) return null;
    return (
        <>
            <Divider />
            <Box>
                <Text fontWeight="medium" mb={2}>
                    Details ({total})
                </Text>
                <Box maxH="240px" overflowY="auto" borderWidth={1} borderColor="gray.200" rounded="md">
                    <List spacing={0}>
                        {nameTruncated && (
                            <IssueRow
                                severity="warning"
                                label="Bezeichnung gekürzt (max. 45 Zeichen)"
                                hasBorder={issues.length > 0}
                            />
                        )}
                        {issues.map((issue, i) => (
                            <IssueRow
                                key={`${issue.line}-${issue.category}-${i}`}
                                severity={ISSUE_SEVERITY[issue.category]}
                                prefix={`Zeile ${issue.line}`}
                                label={ISSUE_LABELS[issue.category]}
                                value={issue.value}
                                // The nr-truncation row already shows the number as its value.
                                nr={issue.category === 'nrTruncated' ? undefined : issue.nr}
                                hasBorder={i < issues.length - 1}
                            />
                        ))}
                    </List>
                </Box>
            </Box>
        </>
    );
}

function IssueRow({
    severity,
    prefix,
    label,
    value,
    nr,
    hasBorder,
}: {
    severity: IssueSeverity;
    prefix?: string;
    label: string;
    value?: string;
    nr?: string;
    hasBorder: boolean;
}) {
    const dot = SEVERITY_DOT[severity];
    return (
        <ListItem
            display="flex"
            alignItems="flex-start"
            px={3}
            py={2}
            borderBottomWidth={hasBorder ? 1 : 0}
            borderColor="gray.100"
            fontSize="sm"
        >
            <Box
                flexShrink={0}
                boxSize={2.5}
                mt={1.5}
                mr={2.5}
                rounded="full"
                bg={dot.color}
                title={dot.title}
                role="img"
                aria-label={dot.title}
            />
            <Box>
                {prefix && (
                    <>
                        <Text as="span" color="gray.500">
                            {prefix}:
                        </Text>{' '}
                    </>
                )}
                <Text as="span">{label}</Text>
                {value && (
                    <Text as="span" color="gray.400">
                        {' '}
                        – „{truncateDisplay(value)}“
                    </Text>
                )}
                {nr && (
                    <Text as="span" color="gray.400">
                        {' '}
                        · Nr. {truncateDisplay(nr)}
                    </Text>
                )}
            </Box>
        </ListItem>
    );
}

/** AWSDate (YYYY-MM-DD) → German DD.MM.YYYY for display. */
function formatDate(iso: string): string {
    const [y, m, d] = iso.split('-');
    return d && m && y ? `${d}.${m}.${y}` : iso;
}

function truncateDisplay(value: string, max = 60): string {
    return value.length > max ? `${value.slice(0, max)}…` : value;
}

function isCsvFile(file: File): boolean {
    return (
        /\.csv$/i.test(file.name) ||
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel'
    );
}
